import { useCallback } from "react";
import { ActionIcon, Badge, Table, Text, Tooltip } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import {
  CommunicationChannel,
  CommunicationLogListItem,
  CommunicationStatus,
} from "../../../services/admin/communicationLog.service";
import { LAB_TIME_ZONE } from "../../../utils/labTime";
import CommunicationRoutingBadge from "./CommunicationRoutingBadge";

const CHANNEL_COLORS: Record<CommunicationChannel, string> = {
  email: "blue",
  hl7: "violet",
  fax: "indigo",
  sms: "teal",
  voice: "grape",
};

const STATUS_COLORS: Record<CommunicationStatus, string> = {
  pending: "gray",
  sent: "blue",
  delivered: "green",
  failed: "red",
  bounced: "orange",
};

interface CommunicationLogTableRowProps {
  log: CommunicationLogListItem;
  onView: (logId: string) => void;
}

function formatInstant(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: LAB_TIME_ZONE,
  });
}

export default function CommunicationLogTableRow({
  log,
  onView,
}: CommunicationLogTableRowProps) {
  const handleView = useCallback(() => onView(log.id), [log.id, onView]);

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{formatInstant(log.sentAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={CHANNEL_COLORS[log.channel]} variant="light" size="sm">
          {log.channel}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{log.communicationType}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>
          {log.recipient}
        </Text>
      </Table.Td>
      <Table.Td>
        <CommunicationRoutingBadge decision={log.routingDecision} />
      </Table.Td>
      <Table.Td>
        <Badge color={STATUS_COLORS[log.status]} variant="light" size="sm">
          {log.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{log.caseNumber || "—"}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{log.triggeredBy?.name || "—"}</Text>
      </Table.Td>
      <Table.Td>
        <Tooltip label="View detail" withArrow>
          <ActionIcon
            variant="subtle"
            onClick={handleView}
            aria-label={`View communication detail for ${log.recipient}`}
          >
            <IconEye size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}
